import { useState, type FormEvent } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/auth/auth-layout";
import { Button } from "@/components/ui/button";
import { Field, FormError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { authClient } from "@/lib/auth-client";
import { authErrorMessage } from "@/lib/i18n-errors";

export const Route = createFileRoute("/_auth/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { t } = useTranslation("auth");
  const { t: tErrors } = useTranslation("errors");
  const { redirect } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    const result = await authClient.requestPasswordReset({
      email: email.trim(),
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setPending(false);
    if (result.error) setError(authErrorMessage(result.error, tErrors));
    else setSentTo(email.trim());
  }

  const back = (
    <Link
      to="/login"
      search={{ redirect }}
      className="text-foreground underline decoration-border-strong underline-offset-4 hover:decoration-foreground"
    >
      {t("forgot.back")}
    </Link>
  );

  if (sentTo) {
    // The same answer whether or not the address has an account, so the form
    // cannot be used to find out who is registered.
    return (
      <AuthLayout
        title={t("forgot.sent")}
        description={t("forgot.sentBody", { email: sentTo })}
        footer={back}
      >
        {null}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("forgot.title")} description={t("forgot.description")} footer={back}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label={t("fields.email")} htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <FormError>{error}</FormError>
        <Button type="submit" disabled={pending}>
          {pending ? <Spinner /> : null}
          {t("forgot.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
